<script>
import { Id, NotificationTimer } from './utils';

const NOTIFICATION_VISIBLE = 1;
const NOTIFICATION_REMOVED = 2;

export default {
  name: 'NotificationGroup',

  inject: ['bus', 'isMobileViewport'],

  props: {
    group: {
      type: String,
      default: '',
    },

    transitionName: {
      type: String,
      default: 'fade',
    },

    maxWidth: {
      type: String,
      default: '30vmax',
    },
  },

  data: () => ({
    list: [],
    cleanupIntervalHandle: null,
  }),

  computed: {
    maxWidthComp() {
      return this.isMobileViewport ? 'auto' : this.maxWidth;
    },

    minWidthComp() {
      if (this.isMobileViewport) {
        return this.$vuetify.breakpoint.width <= 560 ? '85vmin' : '55vmin';
      }

      return '45vmin';
    },

    visible() {
      return this.list.filter((item) => item.state === NOTIFICATION_VISIBLE);
    },

    rootContainer() {
      return this.transitionName ? 'TransitionGroup' : 'div';
    },

    rootAttrs() {
      if (this.transitionName) {
        return {
          tag: 'div',
          name: this.transitionName,
        };
      }

      return null;
    },
  },

  created() {
    this.bus.on('notification:push', this.newNotificationEvent);
  },

  beforeDestroy() {
    this.bus.off('notification:push', this.newNotificationEvent);

    if (!this.cleanupIntervalHandle) {
      clearInterval(this.cleanupIntervalHandle);
      this.cleanupIntervalHandle = null;
    }
  },

  methods: {
    /** @param {import('@/plugins/notifications').NotificationData} data */
    newNotificationEvent(data) {
      let { group, duration, id, text, type, ...rest } = data;
      if (!text)
        return;

      group ??= '';
      duration ??= 5000;
      type ??= 'error';

      if (group !== this.group)
        return;

      const notificationItem = {
        id: id || Id(),
        state: NOTIFICATION_VISIBLE,
        text,
        duration,
        type,
        data: rest,

        listeners: {},

        remove() {
          if (this.timer) {
            clearTimeout(this.timer);
          }

          this.state = NOTIFICATION_REMOVED;
        },
      };

      if (duration > 0) {
        notificationItem.timer = new NotificationTimer(() => notificationItem.remove(), duration);

        notificationItem.listeners.mouseenter = () => {
          if (notificationItem.timer) {
            notificationItem.timer.pause();
          }
        };

        notificationItem.listeners.mouseleave = () => {
          if (notificationItem.timer) {
            notificationItem.timer.resume();
          }
        };
      }

      this.list.push(notificationItem);

      if (!this.cleanupIntervalHandle) {
        this.cleanupIntervalHandle = setInterval(this.cleanupRemoved, 5000);
      }

      return notificationItem.id;
    },

    cleanupRemoved() {
      this.list = this.list.filter((notificationItem) => notificationItem.state !== NOTIFICATION_REMOVED);

      if (this.list.length === 0) {
        clearInterval(this.cleanupIntervalHandle);
        this.cleanupIntervalHandle = null;
      }
    },
  },
};
</script>

<template>
  <component
    :is="rootContainer"
    v-bind="rootAttrs"
    class="notifications-group"
    :style="{
      maxWidth: maxWidthComp,
      minWidth: minWidthComp,
    }"
  >
    <slot name="default" v-bind="{ visible }" />
  </component>
</template>

<style>
.notifications-group {
  position: fixed;
  z-index: 65536;
  display: flex;
  flex-direction: column;
  gap: 9px;
  align-items: center;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
